/* eslint-disable max-lines */
import { ANSWER_THEME_VARIANTS } from 'components/Answer/shared/constants'
import { TAnswerThemeVariant } from 'components/Answer/shared/types'

import couponSageBackgroundImage from 'assets/images/coupon-background-sage.svg'
import couponBackgroundImage from 'assets/images/coupon-background.svg'

import { PageId } from 'page-constants'
import { Cohort, Color, HeaderTitle } from 'root-constants'
import { TValueOf } from 'root-types'

export type TProgressSections = Readonly<Record<string, readonly PageId[]>>

type TPageConfig = {
  isBackButtonDisabled?: boolean
  headerTitle?: string
  headerBackground?: string
  headerTextColor?: string
  isHeaderOverlay?: boolean
  borderColor?: string
  pageBackground?: string
  surfaceBackground?: string
  lightSurfaceBackground?: string
  noteBackground?: string
  bottomBarBackground?: string
  disabledButtonBackground?: string
  couponBackgroundImage?: string
  couponTimerBackground?: string
  hasCardDecisionButtons?: boolean
  progressTrackColor?: string
  hasGlassHeader?: boolean
  hasBottomBarBackground?: boolean
  answerThemeVariant?: TAnswerThemeVariant
  fillsViewport?: boolean
  progressSections?: TProgressSections
}

const TRIGGERS_HEADER_PAGES = [
  PageId.MILLION_SOULS,
  PageId.PORN_TO_COPE,
  PageId.ISSUE_DURATION_V2,
  PageId.FEEL_EXHAUSTED,
  PageId.STAY_FOCUSED,
  PageId.POTENTIAL_BLOCK,
  PageId.FEEL_TIRED,
  PageId.HARD_TO_ACHIEVE,
  PageId.LOST_INTEREST,
  PageId.BOREDOM_SCROLLING,
  PageId.ENERGY_LEVEL_V2,
] as readonly PageId[]

const PATTERNS_HEADER_PAGES = [
  PageId.SLEEP_ISSUES,
  PageId.CONCENTRATION_IMPACT,
  PageId.AFFECTED_ARIA,
  PageId.CURRENT_LIFESTYLE,
  PageId.OVERALL_STATE,
  PageId.REASON_FEELING_WORSE,
  PageId.LAST_TIME_FOCUSED,
  PageId.DANGEROUS_SYMPTOMS,
  PageId.DOPAMINE_EFFECT,
  PageId.THANK_YOU_V2,
] as readonly PageId[]

const GOALS_HEADER_PAGES = [
  PageId.DOPAMINE_BALANCE,
  PageId.SPIRITUAL_ACTIVITIES,
  PageId.DOPAMINE_DETOX,
  PageId.REFERRED_BY_V2,
  PageId.MOTIVATION_LEVEL_V2,
  PageId.ALMOST_THERE_V2,
  PageId.BIBLE_CONTENT_V2,
  PageId.NARRATOR_VOICE_V2,
  PageId.BACKGROUND_MUSIC_V2,
  PageId.TIME_TO_STUDY_BIBLE_V2,
  PageId.PERSONAL_GROWTH_PROGRESS_V3,
  PageId.DEPENDENCE_SUMMARY_V2,
] as readonly PageId[]

const WELLNESS_GRADIENT_PAGES = [
  PageId.DOWNLOAD_WELLNESS,
  PageId.APP_WELLNESS,
] as readonly PageId[]

const WHITE_PAGE_CONFIG = {
  headerBackground: Color.WHITE,
  pageBackground: Color.WHITE,
  progressTrackColor: Color.GRAY_LIGHT,
}

const WELLNESS_PAGE_BACKGROUND =
  'radial-gradient(120.65% 92.67% at 77.73% 31.83%, #FFF2D8 23.48%, #E2EEF8 100%)'

const WHITE_BACKGROUND_PAGES = [PageId.LANDING_WELLNESS] as readonly PageId[]

const CREAM_GRADIENT_PAGE_BACKGROUND =
  'radial-gradient(151.79% 97.81% at 75.33% 32.82%, #FDF3E0 0%, #E2EEF8 100%) #F3F1E9'

export const GRADIENT_BACKGROUND_PAGE_CONFIG: TPageConfig = {
  headerBackground: 'transparent',
  isHeaderOverlay: true,
  hasGlassHeader: true,
  hasBottomBarBackground: false,
  answerThemeVariant: ANSWER_THEME_VARIANTS.WITHOUT_BACKGROUND,
  pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
}

export const PAGE_THEMES = {
  DEFAULT: 'default',
  SAND: 'sand',
} as const

type TPageTheme = TValueOf<typeof PAGE_THEMES>

type TPageThemeConfig = Required<
  Pick<
    TPageConfig,
    | 'headerBackground'
    | 'pageBackground'
    | 'surfaceBackground'
    | 'lightSurfaceBackground'
    | 'noteBackground'
    | 'bottomBarBackground'
    | 'disabledButtonBackground'
    | 'couponBackgroundImage'
    | 'couponTimerBackground'
    | 'hasCardDecisionButtons'
    | 'answerThemeVariant'
  >
>

export const PAGE_THEME_CONFIGS: Record<TPageTheme, TPageThemeConfig> = {
  [PAGE_THEMES.DEFAULT]: {
    headerBackground: Color.SKY_BASE,
    pageBackground: Color.SKY_BASE,
    surfaceBackground: Color.SKY_BASE,
    lightSurfaceBackground: Color.SKY,
    noteBackground: Color.SKY_LIGHTER,
    bottomBarBackground: Color.SKY_BASE,
    disabledButtonBackground: Color.SKY_LIGHT,
    couponBackgroundImage,
    couponTimerBackground: '#d1ece1',
    hasCardDecisionButtons: false,
    answerThemeVariant: ANSWER_THEME_VARIANTS.DEFAULT,
  },
  [PAGE_THEMES.SAND]: {
    headerBackground: Color.SAND_LIGHTEST,
    pageBackground: Color.SAND_LIGHTEST,
    surfaceBackground: Color.SAND_LIGHTEST,
    lightSurfaceBackground: Color.WHITE,
    noteBackground: Color.WHITE,
    bottomBarBackground: Color.SAND_LIGHTEST,
    disabledButtonBackground: Color.SAND_GRAY,
    couponBackgroundImage: couponSageBackgroundImage,
    couponTimerBackground: '#c7e5c2',
    hasCardDecisionButtons: true,
    answerThemeVariant: ANSWER_THEME_VARIANTS.SAND,
  },
}

export const PAGE_THEME_BY_COHORT: Partial<Record<Cohort, TPageTheme>> = {
  [Cohort.AP_KNOWLEDGE_428_C]: PAGE_THEMES.SAND,
}

export const PROGRESS_SECTIONS_BY_COHORT: Partial<
  Record<Cohort, TProgressSections>
> = {
  [Cohort.AP_KNOWLEDGE_428_C]: {
    'onboarding.progressSections.everydayChoices': [PageId.HALAL_HARAM_CARDS],
    'onboarding.progressSections.deeperKnowledge': [PageId.KNOCK_TIMES],
    'onboarding.progressSections.yourJourney': [PageId.LEARN_FIRST],
  },
}

export const KNOWLEDGE_EXTENDED_PAGE_CONFIG: TPageConfig = {
  progressSections: {
    'onboarding.progressSections.aboutYouTitle': [PageId.SELECT_AGE_V3],
    'onboarding.progressSections.deeperKnowledge': [PageId.HALAL_HARAM_CARDS],
    'onboarding.progressSections.yourJourney': [PageId.READ_LISTEN_QURAN],
  },
}

export const PARENTING_QUIZ_V5_PAGE_CONFIG: TPageConfig = {
  progressSections: {
    'onboarding.progressSections.aboutYou': [PageId.SELECT_GENDER_LIST],
    'onboarding.progressSections.parentingChallenges': [
      PageId.DISOBEY_REACTION,
    ],
    'onboarding.progressSections.faithAndSabr': [PageId.QURAN_AT_HOME],
    'onboarding.progressSections.almostDone': [PageId.START_WITH],
  },
}

const GRADIENT_BREAK_PAGES = [
  PageId.BREAK_RIGHT_PLACE_V2,
  PageId.BREAK_NOT_PERFECT,
  PageId.BREAK_NAMING_COURAGE,
  PageId.BREAK_GOAL_NAMED,
] as readonly PageId[]

export const PAGES_CONFIG: { [key in PageId]?: TPageConfig } = {
  ...Object.fromEntries(
    WHITE_BACKGROUND_PAGES.map((pageId) => [pageId, WHITE_PAGE_CONFIG]),
  ),
  ...Object.fromEntries(
    WELLNESS_GRADIENT_PAGES.map((pageId) => [
      pageId,
      {
        isBackButtonDisabled: true,
        headerBackground: 'transparent',
        isHeaderOverlay: true,
        hasGlassHeader: true,
        pageBackground: WELLNESS_PAGE_BACKGROUND,
        progressTrackColor: Color.WHITE,
        fillsViewport: true,
      },
    ]),
  ),
  ...Object.fromEntries(
    GRADIENT_BREAK_PAGES.map((pageId) => [
      pageId,
      {
        headerBackground: 'transparent',
        isHeaderOverlay: true,
        pageBackground:
          'radial-gradient(151.79% 97.81% at 75.33% 32.82%, #FFECC6 0%, #DFE7EE 100%) #F3EAD5',
      },
    ]),
  ),
  ...Object.fromEntries(
    TRIGGERS_HEADER_PAGES.map((pageId) => [
      pageId,
      { headerTitle: HeaderTitle.triggers },
    ]),
  ),
  ...Object.fromEntries(
    PATTERNS_HEADER_PAGES.map((pageId) => [
      pageId,
      { headerTitle: HeaderTitle.patterns },
    ]),
  ),
  ...Object.fromEntries(
    GOALS_HEADER_PAGES.map((pageId) => [
      pageId,
      { headerTitle: HeaderTitle.goals },
    ]),
  ),
  [PageId.INTRO_PRINTABLE]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.ALTERNATIVE_AYAH_LOADER]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    headerTextColor: Color.GOLD_LIGHTER,
    isHeaderOverlay: true,
    pageBackground: Color.BLACK,
  },
  [PageId.INTRO_KARMA_V1]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    headerTextColor: Color.GOLD_LIGHTER,
    isHeaderOverlay: true,
  },
  [PageId.INTRO_AYAH_V1]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    headerTextColor: Color.GOLD_LIGHTER,
    isHeaderOverlay: true,
  },
  [PageId.KNOWLEDGE_QUIZ_INTRO]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    pageBackground: 'linear-gradient(180deg, #E2F1F8 23.03%, #F4F1EC 100%)',
  },
  [PageId.BREAK_HALFWAY]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    pageBackground:
      'linear-gradient(180deg, #B6E4FF 0%, #E4F8F5 40.15%, #F4F1EC 100%)',
  },
  [PageId.INTRO_PRINTABLE_AYAH]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.BUDDHIST_ARCHETYPE]: {
    headerBackground: 'transparent',
    isBackButtonDisabled: true,
    isHeaderOverlay: true,
  },
  [PageId.TAI_CHI_ENERGY_FLOW]: {
    headerBackground: 'transparent',
    isBackButtonDisabled: true,
    isHeaderOverlay: true,
  },
  [PageId.ENERGY_FIELD]: {
    headerBackground: 'transparent',
    isBackButtonDisabled: true,
    isHeaderOverlay: true,
  },
  [PageId.EMOTIONAL_BLOCKAGE]: {
    headerBackground: 'transparent',
    isBackButtonDisabled: true,
    isHeaderOverlay: true,
  },
  [PageId.BREAK_AYAH]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.BREAK_AYAH_2]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.BREAK_AYAH_3]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.ANALYZING_LOADER]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
  },
  [PageId.SELECT_GENDER]: {
    isBackButtonDisabled: true,
  },
  [PageId.SELECT_GENDER_KARMA]: {
    isBackButtonDisabled: true,
  },
  [PageId.SELECT_GENDER_AYAH]: {
    isBackButtonDisabled: true,
  },
  [PageId.AYAH_GENDER]: {
    isBackButtonDisabled: true,
  },
  [PageId.AYAH_GENDER_FACTORY]: {
    isBackButtonDisabled: true,
  },
  [PageId.AYAH_GENDER_V2]: {
    isBackButtonDisabled: true,
  },
  [PageId.SELECT_GENDER_PARENT]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.SELECT_GENDER_ARCHETYPE]: {
    isBackButtonDisabled: true,
  },
  [PageId.SELECT_FAMILY_SIZE]: {
    isBackButtonDisabled: true,
    headerBackground: 'transparent',
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.COMMITMENT_PROMISE]: {
    isHeaderOverlay: false,
  },
  [PageId.FAMILIES_LEARNING]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.LEARNED_RESPONSE]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.LISTEN_TOGETHER]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    hasGlassHeader: true,
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.ANGER_CYCLE]: {
    headerBackground: 'transparent',
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.CALM_FIRST_LESSON]: {
    headerBackground: 'transparent',
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.PARENTING_PROFILE]: {
    headerBackground: 'transparent',
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.JOIN_COMMUNITY]: {
    headerBackground: 'transparent',
    isHeaderOverlay: true,
    pageBackground: CREAM_GRADIENT_PAGE_BACKGROUND,
  },
  [PageId.PURCHASE_LONG]: {
    isBackButtonDisabled: true,
    headerBackground: Color.WHITE,
  },
  [PageId.PURCHASE_LONG_V2]: {
    isBackButtonDisabled: true,
    headerBackground: Color.WHITE,
  },
  [PageId.PURCHASE_LONG_V3]: {
    isBackButtonDisabled: true,
  },
  [PageId.PURCHASE_V3_KARMA_TRIAL]: {
    isBackButtonDisabled: true,
  },
  [PageId.PURCHASE_LONG_V6]: {
    isBackButtonDisabled: true,
  },
  [PageId.PURCHASE_ARCHETYPE_AYAH]: {
    isBackButtonDisabled: true,
  },
  [PageId.WELLNESS_PAYWALL]: {
    ...WHITE_PAGE_CONFIG,
    isBackButtonDisabled: true,
  },
  [PageId.PAYMENT]: {
    isBackButtonDisabled: true,
  },
  [PageId.DOWNLOAD]: {
    isBackButtonDisabled: true,
  },
  [PageId.SIGNUP]: {
    isBackButtonDisabled: true,
  },
  [PageId.ACCOUNT]: {
    isBackButtonDisabled: true,
  },
  [PageId.UPSELL_GUIDE]: {
    isBackButtonDisabled: true,
  },
  [PageId.UPSELL_GUIDE_V2]: {
    isBackButtonDisabled: true,
  },
  [PageId.UPSELL_PODCAST]: {
    isBackButtonDisabled: true,
  },
  [PageId.INAPP_VOICEOVER]: {
    isBackButtonDisabled: true,
    headerBackground: 'linear-gradient(181deg, #FFF2D8 2.86%)',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pageBackground:
      'linear-gradient(181deg, #FFF2D8 2.86%, #FFF 29.99%), linear-gradient(183deg, #FFF2D8 0.15%, #FFF 30.42%)',
  },
  [PageId.INAPP_VOICEOVER_2]: {
    isBackButtonDisabled: true,
    headerBackground: 'linear-gradient(181deg, #FFF2D8 2.86%)',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pageBackground:
      'linear-gradient(181deg, #FFF2D8 2.86%, #FFF 29.99%), linear-gradient(183deg, #FFF2D8 0.15%, #FFF 30.42%)',
  },
  [PageId.INAPP_VOICEOVER_V3]: {
    isBackButtonDisabled: true,
    headerBackground: 'linear-gradient(181deg, #FFF2D8 2.86%)',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pageBackground:
      'linear-gradient(181deg, #FFF2D8 2.86%, #FFF 29.99%), linear-gradient(183deg, #FFF2D8 0.15%, #FFF 30.42%)',
  },
  [PageId.UPSELL_AYAH_PA]: {
    isBackButtonDisabled: true,
  },
  [PageId.CANCEL_OFFER_QUESTION_V1]: {
    isBackButtonDisabled: true,
  },
  [PageId.CANCEL_OFFER_QUESTION_V2]: {
    isBackButtonDisabled: true,
  },
  [PageId.CANCEL_OFFER_QUESTION_V3]: {
    isBackButtonDisabled: true,
  },
  [PageId.CANCEL_OFFER_SUBSCRIPTION_V1]: {
    isBackButtonDisabled: true,
  },
  [PageId.CANCEL_OFFER_SUBSCRIPTION_V5]: {
    borderColor: 'rgba(0, 0, 0, 0.18)',
    isBackButtonDisabled: true,
  },
  [PageId.UPSELL_CANCEL_OFFER_V1]: {
    isBackButtonDisabled: true,
  },
  [PageId.UPSELL_CANCEL_QUESTION_AYAH]: {
    isBackButtonDisabled: true,
  },
}
